import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import PortalShell from "@/components/PortalShell";

function Stat({ label, value, icon }) {
  return (
    <div className="brut-card !p-5">
      <div className="flex items-center justify-between">
        <div className="font-mono-tech text-[10px] uppercase tracking-widest text-neutral-500">{label}</div>
        <i className={`${icon} text-[#ffd600]`} />
      </div>
      <div className="font-display text-5xl text-white font-semibold mt-3">{value}</div>
    </div>
  );
}

export function AdminDashboard() {
  const [quotes, setQuotes] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [users, setUsers] = useState([]);

  useEffect(() => {
    api.get("/admin/quotes").then((r) => setQuotes(r.data)).catch(() => {});
    api.get("/admin/contacts").then((r) => setContacts(r.data)).catch(() => {});
    api.get("/admin/users").then((r) => setUsers(r.data)).catch(() => {});
  }, []);

  return (
    <PortalShell role="admin">
      <div data-testid="admin-dashboard">
        <div className="eyebrow mb-2">/ Administration</div>
        <h1 className="font-display uppercase font-bold text-4xl md:text-5xl tracking-tight">Vue d’ensemble</h1>
        <p className="text-neutral-400 mt-3">Activité récente du site DCR-BATI.</p>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8">
          <Stat label="Demandes devis" value={quotes.length} icon="fa-solid fa-file-signature" />
          <Stat label="Messages contact" value={contacts.length} icon="fa-solid fa-inbox" />
          <Stat label="Clients" value={users.filter(u => u.role === "client").length} icon="fa-solid fa-users" />
          <Stat label="Admins" value={users.filter(u => u.role === "admin").length} icon="fa-solid fa-user-shield" />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-10">
          <div className="brut-card">
            <div className="flex items-center justify-between mb-5">
              <div className="font-display uppercase text-xl">Derniers devis</div>
              <Link to="/admin/devis" className="font-mono-tech text-xs text-[#ffd600] uppercase tracking-widest">Tout voir →</Link>
            </div>
            <div className="space-y-3" data-testid="recent-quotes">
              {quotes.length === 0 && <div className="text-neutral-500 text-sm">Aucune demande pour le moment.</div>}
              {quotes.slice(0, 5).map((q) => (
                <div key={q.id} className="border border-[#262626] p-4">
                  <div className="flex items-center justify-between">
                    <div className="font-display uppercase">{q.name}</div>
                    <div className="font-mono-tech text-[10px] uppercase tracking-widest text-[#ffd600]">{q.project_type}</div>
                  </div>
                  <div className="text-neutral-400 text-sm mt-1">{q.email} · {q.phone}</div>
                  <div className="text-neutral-500 text-sm mt-1 line-clamp-2">{q.description}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="brut-card">
            <div className="flex items-center justify-between mb-5">
              <div className="font-display uppercase text-xl">Derniers messages</div>
              <Link to="/admin/contacts" className="font-mono-tech text-xs text-[#ffd600] uppercase tracking-widest">Tout voir →</Link>
            </div>
            <div className="space-y-3" data-testid="recent-contacts">
              {contacts.length === 0 && <div className="text-neutral-500 text-sm">Aucun message pour le moment.</div>}
              {contacts.slice(0, 5).map((c) => (
                <div key={c.id} className="border border-[#262626] p-4">
                  <div className="flex items-center justify-between">
                    <div className="font-display uppercase">{c.name}</div>
                    <div className="font-mono-tech text-[10px] uppercase tracking-widest text-neutral-400">{c.subject || "Sans objet"}</div>
                  </div>
                  <div className="text-neutral-400 text-sm mt-1">{c.email}</div>
                  <div className="text-neutral-500 text-sm mt-1 line-clamp-2">{c.message}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </PortalShell>
  );
}

export function AdminDevis() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/admin/quotes").then((r) => setItems(r.data)); }, []);
  return (
    <PortalShell role="admin">
      <div data-testid="admin-devis-list">
        <h1 className="font-display uppercase font-bold text-4xl tracking-tight">Demandes de devis</h1>
        <div className="mt-8 overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr className="border-b border-white/5">
                {["Date", "Nom", "Type", "Lieu", "Surface", "Contact", "Description"].map((h) => (
                  <th key={h} className="text-left font-mono-tech text-[10px] uppercase tracking-widest text-neutral-500 py-3 px-3">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && (
                <tr><td colSpan={7} className="text-neutral-500 py-5 px-3">Aucune demande.</td></tr>
              )}
              {items.map((q) => (
                <tr key={q.id} className="border-b border-white/5 hover:bg-white/[0.02]">
                  <td className="py-4 px-3 font-mono-tech text-xs text-neutral-400">{new Date(q.created_at).toLocaleDateString("fr-FR")}</td>
                  <td className="py-4 px-3"><div className="font-display uppercase">{q.name}</div>{q.company && <div className="text-xs text-neutral-500">{q.company}</div>}</td>
                  <td className="py-4 px-3"><span className="font-mono-tech text-[10px] uppercase tracking-widest text-[#ffd600]">{q.project_type}</span></td>
                  <td className="py-4 px-3 text-neutral-300">{q.location || "—"}</td>
                  <td className="py-4 px-3 text-neutral-300">{q.surface || "—"}</td>
                  <td className="py-4 px-3 text-xs text-neutral-400">{q.email}<br />{q.phone}</td>
                  <td className="py-4 px-3 text-neutral-300 max-w-md"><div className="line-clamp-3 text-sm">{q.description}</div></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </PortalShell>
  );
}

export function AdminContacts() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/admin/contacts").then((r) => setItems(r.data)); }, []);
  return (
    <PortalShell role="admin">
      <div data-testid="admin-contacts-list">
        <h1 className="font-display uppercase font-bold text-4xl tracking-tight">Messages de contact</h1>
        <div className="mt-8 space-y-3">
          {items.length === 0 && <div className="text-neutral-500">Aucun message.</div>}
          {items.map((c) => (
            <div key={c.id} className="brut-card">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <div className="font-display uppercase text-lg">{c.name} <span className="text-neutral-500 text-sm normal-case font-mono-tech">· {c.email}</span></div>
                  {c.subject && <div className="text-[#ffd600] font-mono-tech text-xs uppercase tracking-widest mt-1">{c.subject}</div>}
                </div>
                <div className="font-mono-tech text-xs text-neutral-500">{new Date(c.created_at).toLocaleString("fr-FR")}</div>
              </div>
              <p className="text-neutral-300 mt-4 whitespace-pre-wrap">{c.message}</p>
              {c.phone && <div className="font-mono-tech text-xs text-neutral-500 mt-3">Tél : {c.phone}</div>}
            </div>
          ))}
        </div>
      </div>
    </PortalShell>
  );
}

export function AdminChantiers() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/projects").then((r) => setItems(r.data)); }, []);
  return (
    <PortalShell role="admin">
      <div data-testid="admin-chantiers-list">
        <h1 className="font-display uppercase font-bold text-4xl tracking-tight">Chantiers</h1>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-8">
          {items.map((p) => (
            <Link key={p.id} to={`/admin/chantier/${p.id}`} className="brut-card" data-testid={`admin-project-${p.id}`}>
              <div className="font-display uppercase text-xl">{p.name}</div>
              <div className="font-mono-tech text-xs uppercase tracking-widest text-neutral-500 mt-1">{p.location}</div>
              <div className="text-neutral-400 text-sm mt-2 line-clamp-2">{p.description}</div>
              <div className="mt-4">
                <div className="h-2 bg-neutral-800 relative">
                  <div className="absolute top-0 left-0 h-full bg-[#ffd600]" style={{ width: `${p.progress}%` }} />
                </div>
                <div className="font-mono-tech text-xs text-[#ffd600] mt-2">{p.progress}% d’avancement</div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </PortalShell>
  );
}
